/**
 * Class names for the front door (`/auth/login`, `/auth/register`,
 * `/portal/login`), from the front-door design handoff. The family sign-in
 * form uses them too.
 */

/**
 * Applied on a form: 50px fields, 15px/600 labels and the lavender focus ring,
 * including the phone field's input and country button.
 */
export const FRONT_DOOR_FIELD_STYLES = [
  "[&_input]:h-[50px]",
  "[&_input]:border-[#d3d9dc]",
  "[&_input]:bg-white",
  "[&_input]:px-4",
  "[&_input]:text-base",
  "[&_input]:text-[#1a1a1a]",
  "[&_input::placeholder]:text-[#96a1a8]",
  "[&_input:not([type=tel])]:rounded-[10px]",
  "[&_input[type=tel]]:rounded-e-[10px]",
  "[&_input[type=tel]]:rounded-s-none",
  "[&_.PhoneInput_button]:h-[50px]",
  "[&_.PhoneInput_button]:rounded-s-[10px]",
  "[&_.PhoneInput_button]:border-[#d3d9dc]",
  "[&_input:focus-visible]:border-[#7d6fd0]",
  "[&_input:focus-visible]:ring-[3px]",
  "[&_input:focus-visible]:ring-[rgba(125,111,208,0.2)]",
  "[&_input:focus-visible]:ring-offset-0",
  "[&_input:disabled]:bg-[#f6f7f8]",
  "[&_input:disabled]:opacity-100",
  "[&_button[role=combobox]]:h-[50px]",
  "[&_button[role=combobox]]:rounded-[10px]",
  "[&_button[role=combobox]]:border-[#d3d9dc]",
  "[&_button[role=combobox]]:bg-white",
  "[&_button[role=combobox]]:text-base",
  "[&_button[role=combobox]:focus]:border-[#7d6fd0]",
  "[&_button[role=combobox]:focus]:ring-[3px]",
  "[&_button[role=combobox]:focus]:ring-[rgba(125,111,208,0.2)]",
  "[&_button[role=combobox]:focus]:ring-offset-0",
  "[&_label]:text-[15px]",
  "[&_label]:font-semibold",
  "[&_label]:text-[#1a1a1a]",
].join(" ");

export const FRONT_DOOR_HELP_CLASS =
  "text-[13px] leading-normal text-[#6b6b6b]";

export const FRONT_DOOR_PANEL_TITLE_CLASS =
  "font-martina text-2xl font-medium leading-[1.12] tracking-[-0.02em] text-[#1a1a1a]";

export const FRONT_DOOR_PRIMARY_BUTTON_CLASS =
  "h-[50px] w-full gap-2 rounded-xl md:rounded-xl border border-[#bcb3ef] bg-[#dcd6f8] text-[15px] font-medium text-[#1a1a1a] hover:bg-[#d0c9f5] focus-visible:ring-2 focus-visible:ring-[#7d6fd0] focus-visible:ring-offset-2";

export const FRONT_DOOR_SECONDARY_BUTTON_CLASS =
  "h-[50px] w-full gap-2 rounded-xl md:rounded-xl border border-[#ddd8f5] bg-white text-[15px] font-medium text-[#1a1a1a] hover:bg-[#f5f3fe] focus-visible:ring-2 focus-visible:ring-[#7d6fd0] focus-visible:ring-offset-2";

/** A text button that steps back, underlined in grey. */
export const FRONT_DOOR_QUIET_BUTTON_CLASS =
  "text-[15px] text-[#6b6b6b] underline decoration-[#d3d9dc] underline-offset-4 hover:text-[#1a1a1a] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7d6fd0] rounded-sm";

export const FRONT_DOOR_LINK_CLASS =
  "text-[#1c7aa3] hover:text-[#2496c6] hover:underline focus-visible:outline-none focus-visible:underline";

export const FRONT_DOOR_PILL_CLASS =
  "inline-flex shrink-0 items-center whitespace-nowrap rounded-full border px-[15px] py-[9px] text-[13.5px] text-[#1a1a1a] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7d6fd0] focus-visible:ring-offset-2";
export const FRONT_DOOR_PILL_SELECTED_CLASS =
  "border-[#bcb3ef] bg-[#dcd6f8] font-semibold";
export const FRONT_DOOR_PILL_UNSELECTED_CLASS =
  "border-[#ddd8f5] bg-white font-medium text-[#4a4a4a] hover:bg-[#f5f3fe]";

export const FRONT_DOOR_TAB_CLASS =
  "h-10 flex-1 rounded-[9px] border text-[14.5px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7d6fd0]";
export const FRONT_DOOR_TAB_SELECTED_CLASS =
  "border-[#ddd8f5] bg-white font-semibold text-[#1a1a1a]";
export const FRONT_DOOR_TAB_UNSELECTED_CLASS =
  "border-transparent bg-transparent font-medium text-[#6b6b6b] hover:text-[#1a1a1a]";

/** A page's one card: white, lavender hairline, no shadow. */
export const FRONT_DOOR_CARD_CLASS =
  "rounded-2xl border-[1.5px] border-[#ddd8f5] bg-white p-5 md:p-[30px]";

/** A card's title row: Martina on the left, an optional step on the right. */
export const FRONT_DOOR_CARD_TITLE_CLASS =
  "font-martina text-[22px] font-medium leading-tight tracking-[-0.02em] text-[#1a1a1a]";
export const FRONT_DOOR_EYEBROW_CLASS =
  "font-mono text-[11.5px] font-medium uppercase tracking-[0.12em] text-[#6b6b6b]";
